import { apiClient } from '@/shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';
import { getClinicPortalServiceTypeHeader } from '@/hooks/main/clinicPortalServiceType';

// ==================== Types ====================

export interface RezeptInvoiceListItem {
  id: number;
  patient_id: number;
  serial_number: number | string;
  patient_name: string;
  insurer_number: string | null;
  insurer_name: string | null;
  treatement_yearmonth: number | string;
  treatement_first_date: string | null;
  rezept_kbn: number | null;
  status: number | null;
  is_rezept_kbn2: boolean;
  massager_id: number | null;
  // A2-only — null when tenant is R2
  print_invoice: number | null;
  free_medical_treatment_name: string | null;
  free_medical_treatment_fee: number | null;
  is_special_area_addition: boolean | null;
  part_of_charge: number | string | null;
}

export interface RezeptInvoiceListFilters {
  search_term_type: 1 | 2;
  search_treatement_from?: string | null;
  search_treatement_to?: string | null;
  search_treatement_one?: string | null;
  massager_id?: number | null;
  sort_radio?: 1 | 2 | 3 | 4;
  /** HIT-14683: header-column sort over the whole result (server-side, before paging). */
  sort_by?: 'serial_number' | 'patient_name' | 'insurer_number' | 'insurer_name';
  sort_dir?: 'asc' | 'desc';
  page?: number;
  per_page?: number;
  rezept_kbn?: number;
  pdf_pattern?: '1' | '2' | '3' | '4' | null;
  is_return?: boolean | null;
  target_rtn?: boolean | null;
  target_stical?: boolean | null;
  serial_number?: number | null;
  patient_name?: string | null;
  insurer_name?: string | null;
  insurer_type_id?: number | null;
  syokanFlg?: boolean | null;
  hide_67?: boolean | null;
  ini?: string | null;
  patient_id?: number | null;
}

export interface RezeptInvoiceListPagination {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number | null;
  to: number | null;
}

export interface RezeptInvoiceListResponse {
  success: boolean;
  data: {
    data: RezeptInvoiceListItem[];
    pagination: RezeptInvoiceListPagination;
    /**
     * Cross-page rezept id list for the checkbox header (全 / 反 buttons).
     * Includes every rezept matching the search criteria, not just the
     * current page.
     */
    all_ids: number[];
  };
}

// ==================== API ====================

function serviceTypeHeaders(): Record<string, string> {
  return { 'X-Service-Type': getClinicPortalServiceTypeHeader() };
}

export async function fetchRezeptInvoiceList(
  siteType: string,
  clinicNumber: string,
  filters: RezeptInvoiceListFilters
): Promise<RezeptInvoiceListResponse> {
  const response = await apiClient.post<RezeptInvoiceListResponse>(
    clinicApiPath(siteType, clinicNumber, '/rezepts/invoices-list'),
    filters,
    { headers: serviceTypeHeaders() }
  );
  return response.data;
}

// ==================== Init endpoint ====================

export interface RezeptInvoiceMassagerOption {
  id: number;
  serial_number: number | string | null;
  name: string | null;
}

export interface RezeptInvoiceInitDefaults {
  search_term_type: 1 | 2;
  search_treatement_from: string;
  search_treatement_to: string;
  search_treatement_one: string;
}

export interface RezeptInvoiceInitResponse {
  success: boolean;
  data: {
    defaults: RezeptInvoiceInitDefaults;
  };
}

export async function fetchRezeptInvoiceInit(
  siteType: string,
  clinicNumber: string
): Promise<RezeptInvoiceInitResponse> {
  const response = await apiClient.get<RezeptInvoiceInitResponse>(
    clinicApiPath(siteType, clinicNumber, '/rezepts/invoices-init'),
    { headers: serviceTypeHeaders() }
  );
  return response.data;
}

// ==================== Massager dropdown (per-month) ====================

export interface RezeptInvoiceMassagersResponse {
  success: boolean;
  data: {
    massagers: RezeptInvoiceMassagerOption[];
  };
}

/**
 * Massagers who have at least one rezept whose treatement_yearmonth equals
 * the given YYYYMM. The list reloads on the FE when the user changes
 * 施術開始期 (search_treatement_from).
 */
export async function fetchRezeptInvoiceMassagers(
  siteType: string,
  clinicNumber: string,
  treatementYearmonth: string
): Promise<RezeptInvoiceMassagersResponse> {
  const response = await apiClient.get<RezeptInvoiceMassagersResponse>(
    clinicApiPath(siteType, clinicNumber, '/rezepts/invoices-massagers'),
    {
      params: { treatement_yearmonth: treatementYearmonth },
      headers: serviceTypeHeaders(),
    }
  );
  return response.data;
}

// ==================== Invoice list PDF ====================

export interface RezeptInvoiceListPdfPayload {
  rezept_ids: number[];

  print_receipt: boolean;
  print_invoice: boolean;
  print_receipt_invoice: boolean;

  print_type: 1 | 2 | 3;

  search_term_type?: 1 | 2;

  print_receipt_100_sub1: boolean;
  print_receipt_100_sub2: boolean;
  print_receipt_100_sub3: boolean;

  option_1: boolean;
  option_2: boolean;
  option_3: boolean;
  option_4: boolean;
  option_5: boolean;
  option_6: boolean;
  option_7: boolean;
  option_8: boolean;
  option_9: boolean;
  option_11: boolean;

  option_date?: string;

  // Search period — used by BE to judge 施術報告書交付料 trigger:
  //   月別 (search_term_type=1): treat [from, to] as the printing period.
  //   日別 (search_term_type=2): FE mirrors selected day into from = to.
  search_treatement_from?: string;
  search_treatement_to?: string;
}

// ==================== Free medical types (編集 modal dropdown) ====================

export interface RezeptFreeMedicalType {
  id: number;
  name: string;
  price: number;
  category_name: string | null;
  type_name: string | null;
}

export interface RezeptFreeMedicalTypesResponse {
  success: boolean;
  data: { types: RezeptFreeMedicalType[] };
}

export async function fetchRezeptFreeMedicalTypes(
  siteType: string,
  clinicNumber: string
): Promise<RezeptFreeMedicalTypesResponse> {
  const response = await apiClient.get<RezeptFreeMedicalTypesResponse>(
    clinicApiPath(siteType, clinicNumber, '/rezepts/free-medical-types'),
    { headers: serviceTypeHeaders() }
  );
  return response.data;
}

export interface SaveRezeptFreeMedicalPayload {
  free_medical_treatment_name: string | null;
  free_medical_treatment_fee: number | null;
  /** 日別 treatment day (YYYY-MM-DD) the 保険外 belongs to. */
  visit_date: string;
}

export async function saveRezeptFreeMedical(
  siteType: string,
  clinicNumber: string,
  rezeptId: number,
  payload: SaveRezeptFreeMedicalPayload
): Promise<void> {
  await apiClient.put(
    clinicApiPath(siteType, clinicNumber, `/rezepts/${rezeptId}/free-medical`),
    payload,
    { headers: serviceTypeHeaders() }
  );
}

// ==================== Reset print invoice ====================

export async function resetRezeptPrintInvoice(
  siteType: string,
  clinicNumber: string,
  rezeptId: number
): Promise<void> {
  await apiClient.put(
    clinicApiPath(siteType, clinicNumber, `/rezepts/${rezeptId}/reset-print-invoice`),
    {},
    { headers: serviceTypeHeaders() }
  );
}

/**
 * POST `/rezepts/invoice-list-pdf` returns a short-lived preview URL (ticket-based).
 * Open the returned URL in a new tab so the browser uses the server-supplied
 * `Content-Disposition` filename (`invoice.pdf`) when the user clicks Save.
 *
 * Previous flow returned a Blob and the FE built a `blob:UUID` URL — that
 * caused the Save dialog to suggest `UUID.pdf` instead of `invoice.pdf`.
 */
export async function downloadRezeptInvoiceListPdf(
  siteType: string,
  clinicNumber: string,
  payload: RezeptInvoiceListPdfPayload
): Promise<string> {
  const response = await apiClient.post<{ data: { url: string } }>(
    clinicApiPath(siteType, clinicNumber, '/rezepts/invoice-list-pdf'),
    payload,
    { headers: serviceTypeHeaders() }
  );
  return response.data.data.url;
}

// ==================== 請求患者一覧 PDF (button on /rezepts/init) ====================

/**
 * Filter payload for 請求患者一覧 PDF — mirrors the rezept list search shape.
 * BD: button ignores any checkbox selection and re-runs the search filter.
 */
export type BillPatientListPdfPayload = Record<string, unknown>;

/**
 * POST `/rezepts/bill-patient-list-pdf` — streams PDF binary inline.
 * Returns null when BE responds 204 (no rezepts match the filter) so the
 * caller can show the "該当するデータはありませんでした。" popup.
 */
export async function downloadBillPatientListPdf(
  siteType: string,
  clinicNumber: string,
  payload: BillPatientListPdfPayload
): Promise<Blob | null> {
  const res = await apiClient.post(
    clinicApiPath(siteType, clinicNumber, '/rezepts/bill-patient-list-pdf'),
    payload,
    { headers: serviceTypeHeaders(), responseType: 'blob' }
  );
  if (res.status === 204) return null;
  return res.data as Blob;
}

// ==================== Format PDF (フォーマット印刷 — blank receipt template PDF) ====================

/**
 * Payload for フォーマット印刷.
 * Per BD: prints **blank** receipt templates (no rezept data filled in) so the
 * user can print them out and write on physically. Therefore we send only the
 * print form selection + paper size + header options — never `rezept_ids`.
 */
export type RezeptFormatPdfPayload = Omit<RezeptInvoiceListPdfPayload, 'rezept_ids'>;

export async function downloadRezeptFormatPdf(
  siteType: string,
  clinicNumber: string,
  payload: RezeptFormatPdfPayload
): Promise<string> {
  const response = await apiClient.post<{ data: { url: string } }>(
    clinicApiPath(siteType, clinicNumber, '/rezepts/format-pdf'),
    payload,
    { headers: serviceTypeHeaders() }
  );
  return response.data.data.url;
}

/**
 * 令和8年法改正 No 3 — 月別明細書発行依頼書（別紙様式5）の雛型印刷.
 *
 * Slip the patient signs to request the itemised statement monthly. Only the 宛名 can be
 * filled: pass the 拠点 picked in the 「月別希望」 popup (HIT-13991); omit it for a blank
 * 宛名. Shared by A2 and R2 — the form's wording is identical.
 */
export async function fetchMonthlyStatementRequestForm(
  siteType: string,
  clinicNumber: string,
  clinicLocationId?: number | null
): Promise<Blob> {
  const response = await apiClient.get(
    clinicApiPath(siteType, clinicNumber, '/rezepts/monthly-statement-request-form'),
    {
      headers: { 'X-Service-Type': getClinicPortalServiceTypeHeader() },
      params: clinicLocationId ? { clinic_location_id: clinicLocationId } : undefined,
      responseType: 'blob',
    }
  );
  return response.data as Blob;
}

export interface MonthlyStatementRequestFormInitParams {
  massager_id?: number | null;
  search_treatement_from?: string | null;
  search_treatement_to?: string | null;
}

export interface MonthlyStatementRequestFormInit {
  /** In id order; shown as {serial_number}:{location_name}. */
  clinic_locations: { id: number; serial_number: number | null; location_name: string }[];
  /** 拠点 of the searched massager when unambiguous; null → 「指定なし」. */
  default_clinic_location_id: number | null;
}

/** 「月別希望」 popup options: the clinic's 拠点 and the default picked from the search. */
export async function fetchMonthlyStatementRequestFormInit(
  siteType: string,
  clinicNumber: string,
  params: MonthlyStatementRequestFormInitParams
): Promise<MonthlyStatementRequestFormInit> {
  const response = await apiClient.get<{ data: MonthlyStatementRequestFormInit }>(
    clinicApiPath(siteType, clinicNumber, '/rezepts/monthly-statement-request-form/init'),
    {
      headers: { 'X-Service-Type': getClinicPortalServiceTypeHeader() },
      params: {
        massager_id: params.massager_id || undefined,
        search_treatement_from: params.search_treatement_from || undefined,
        search_treatement_to: params.search_treatement_to || undefined,
      },
    }
  );
  return response.data.data;
}

/**
 * 令和8年法改正 No 7 — 訪問施術総括表.
 *
 * Two calls because the button answers two questions at different moments: whether to
 * show it at all (the month billed 訪問施術料4/5 somewhere), and whether printing is
 * allowed yet (every rezept of the submission month is 確定).
 */
export async function fetchVisitSummaryAvailability(
  siteType: string,
  clinicNumber: string,
  params: { search_yearmonth?: string; search_visitmonth?: string }
): Promise<boolean> {
  const response = await apiClient.get(
    clinicApiPath(siteType, clinicNumber, '/rezepts/visit-summary-availability'),
    { headers: { 'X-Service-Type': getClinicPortalServiceTypeHeader() }, params }
  );
  return Boolean(
    (response.data as { data?: { has_visit_summary?: boolean } })?.data?.has_visit_summary
  );
}

export async function fetchVisitSummaryPdf(
  siteType: string,
  clinicNumber: string,
  params: { search_yearmonth?: string; search_visitmonth?: string }
): Promise<Blob> {
  const response = await apiClient.get(
    clinicApiPath(siteType, clinicNumber, '/rezepts/visit-summary-pdf'),
    {
      headers: { 'X-Service-Type': getClinicPortalServiceTypeHeader() },
      params,
      responseType: 'blob',
    }
  );
  return response.data as Blob;
}
